import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/not-found.css";

/* ---------- Content: edit these ---------- */
const links = [
  { icon: "home", title: "Home", text: "Start from the beginning", to: "/" },
  {
    icon: "about",
    title: "About us",
    text: "Learn who we are",
    to: "/about-us",
  },
  {
    icon: "blog",
    title: "Blogs",
    text: "Read our latest insights",
    to: "/blogs",
  },
  { icon: "careers", title: "Careers", text: "Join our team", to: "/careers" },
  {
    icon: "faq",
    title: "FAQs",
    text: "Quick answers to common questions",
    to: "/faqs",
  },
  {
    icon: "contact",
    title: "Contact us",
    text: "We are happy to help",
    to: "/contact-us",
  },
];

/* ---------- Icons ---------- */
const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
};
const Icon = {
  arrow: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="2">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  back: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="2">
      <path d="M19 12H5M11 6l-6 6 6 6" />
    </svg>
  ),
  home: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <path d="M3 11l9-7 9 7M5 10v9h5v-5h4v5h5v-9" />
    </svg>
  ),
  about: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </svg>
  ),
  blog: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <path d="M5 4h11l3 3v13H5zM9 11h6M9 15h6" />
    </svg>
  ),
  careers: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <rect x="3" y="7" width="18" height="13" rx="3" />
      <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M3 13h18" />
    </svg>
  ),
  faq: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .9-1 1.7M12 17h.01" />
    </svg>
  ),
  contact: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <path d="M3 6h18v12H3zM3 7l9 6 9-6" />
    </svg>
  ),
};

/* The "0" of 404: a ring with a "no signal" icon */
function Ring() {
  return (
    <span className="gt9-ring" aria-hidden="true">
      <svg viewBox="0 0 120 120">
        <defs>
          <linearGradient id="gt9-ringg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#f1d6a4" />
            <stop offset="1" stopColor="#b98b4e" />
          </linearGradient>
        </defs>
        <g className="spin">
          <circle
            cx="60"
            cy="60"
            r="57"
            fill="none"
            stroke="#d2a566"
            strokeOpacity=".5"
            strokeWidth="1.6"
            strokeDasharray="2 7"
          />
        </g>
        <circle
          cx="60"
          cy="60"
          r="45"
          fill="none"
          stroke="url(#gt9-ringg)"
          strokeWidth="11"
        />
        <path
          d="M45 66a21 21 0 0 1 30 0M52.5 73.5a10.5 10.5 0 0 1 15 0"
          fill="none"
          stroke="#fff"
          strokeWidth="5"
          strokeLinecap="round"
        />
        <circle cx="60" cy="82" r="3.6" fill="#fff" />
        <path
          d="M34 34L86 86"
          stroke="#d2a566"
          strokeWidth="6"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

export default function NotFound() {
  const navigate = useNavigate();

  useEffect(() => {
    const previous = document.title;
    document.title = "Page not found – Global Tech9 LLC";
    return () => {
      document.title = previous;
    };
  }, []);

  // previous page if there is one, otherwise the home page
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <main className="gt9-nf">
      <section className="gt9-nf__hero">
        <i className="gt9-dot" />
        <i className="gt9-dot" />
        <i className="gt9-dot" />
        <i className="gt9-dot" />
        <i className="gt9-dot" />
        <div className="gt9-container">
          <span className="gt9-eyebrow">Error 404</span>
          <div className="gt9-num" role="img" aria-label="404">
            <span>4</span>
            <Ring />
            <span>4</span>
          </div>
          <h1 className="gt9-nf__title">
            This page took a <span className="hl">wrong turn</span>
          </h1>
          <p className="gt9-nf__lead">
            The page you are looking for may have been moved, renamed or no
            longer exists. Let's get you back on track.
          </p>
          <div className="gt9-actions">
            <Link to="/" className="gt9-btn gt9-btn--gold">
              Back to home {Icon.arrow}
            </Link>
            <button
              type="button"
              className="gt9-btn gt9-btn--ghost"
              style={{ boxShadow: "unset" }}
              onClick={goBack}
            >
              {Icon.back} Go back
            </button>
          </div>
        </div>
      </section>

      <div className="gt9-nf__body">
        <div className="gt9-container">
          <section className="gt9-panel" aria-labelledby="gt9-nf-links">
            <div className="gt9-panel__head">
              <div>
                <span className="gt9-eyebrow">Quick links</span>
                <h2 id="gt9-nf-links">Try one of these instead</h2>
              </div>
              <p>Jump straight to the part of the site you need.</p>
            </div>
            <div className="gt9-tiles">
              {links.map((l) => (
                <Link className="gt9-tile" to={l.to} key={l.to}>
                  <i>{Icon[l.icon]}</i>
                  <div>
                    <b>{l.title}</b>
                    <span>{l.text}</span>
                  </div>
                  {Icon.arrow}
                </Link>
              ))}
            </div>
          </section>
          <p className="gt9-note">
            Think this link should work? Tell us at{" "}
            <a href="mailto:support@globaltech9.com">support@globaltech9.com</a>{" "}
            and we will fix it.
          </p>
        </div>
      </div>
    </main>
  );
}
